/**
 * Response shapes for MCP's admin API — all 17 endpoints.
 *
 * Taken from the LIVE dev API and cross-checked against
 * docs/products/mcp.md, which documents what each field means
 * and which of them mislead.
 *
 * NOTE how `business_id` is handled: it is SEARCHABLE BUT NEVER RENDERED.
 *
 * The API sends it on several responses. Ops know merchants by
 * `business_reference`, and the reference doc is explicit that a console must
 * never surface the UUID — a UUID beside a reference number invites copying
 * the wrong one into a ticket, and nobody can read one aloud.
 *
 * It was previously absent from these types entirely, so that using it was a
 * compile error. That guard cost us the one case it should have allowed: the
 * entitlements table keys on this id, so a UUID pasted out of a DynamoDB
 * console matched nothing at all — the place an operator is most likely to be
 * copying from was the place the console could not help (S0.1 / DA-3335).
 *
 * So the field now exists and is typed `SearchOnly`, which is a string that
 * cannot be assigned to a `string` parameter or rendered as a ReactNode
 * without an explicit cast. Matching on it costs nothing; displaying it still
 * has to be a deliberate act a reviewer can see.
 */

/**
 * A value the console may MATCH on but must not DISPLAY.
 *
 * The brand makes rendering a type error rather than a review question —
 * `{tenant.business_id}` does not compile, while
 * `searchText.includes(business_id)` does, because `searchTextOf` unwraps it
 * once, in one place, on purpose.
 */
export type SearchOnly = string & { readonly __searchOnly: unique symbol };

/** Unwrap a search-only value at the single site allowed to read it. */
export function searchOnlyValue(v: SearchOnly | undefined | null): string {
  return typeof v === "string" ? v : "";
}

import type { Tone } from "../components/tone";

// ---------------------------------------------------------------- tenants --

export interface McpTenantSummary {
  /** What ops know a merchant by. */
  business_reference: string;
  business_name: string;
  email: string;
  /**
   * The entitlements table's key. Searchable, never rendered — see the module
   * docstring. Optional because not every response carries it.
   */
  business_id?: SearchOnly;
  /**
   * The STORED tier, or null when the merchant has no entitlements row.
   *
   * Null is not "free". The server treats a merchant with no row AS free when
   * resolving caps, but nothing has been stored — a read never creates the
   * row. Rendering null as "free" would tell an operator a decision had been
   * made that never was, and hide the difference between a deliberate free
   * tier and a merchant nobody has touched.
   */
  tier: string | null;
  /** Merchant-wide block — the ("<ref>", "*") row. NOT set by a per-user block. */
  blocked: boolean;
  /**
   * Emails blocked at THIS merchant specifically — being REMOVED from this
   * response, and already absent in dev.
   *
   * MCP is moving the names off the fleet list and onto tenant detail, leaving
   * a count here. Both fields are optional because the two environments are
   * mid-rollout: dev sends the count, prod still sends the array, and a console
   * that assumed either one crashes against the other. It crashed against dev —
   * `t.blocked_users.length` threw on Overview, the tenant list and Blocks &
   * suspensions, which is what the error boundary was catching.
   *
   * Read the count through `blockedUserCount`, never these fields directly.
   */
  blocked_users?: string[];
  /** Replaces `blocked_users` on this response. See above. */
  blocked_users_count?: number;
}

/**
 * How many users are blocked at this merchant, whichever shape the API sent.
 *
 * The count when it is there, the array's length while it still is, and 0 when
 * neither — because "the field is gone" is not "nobody is blocked", and the
 * only honest floor is the one that claims nothing.
 */
export function blockedUserCount(t: {
  blocked_users?: string[];
  blocked_users_count?: number;
}): number {
  return t.blocked_users_count ?? t.blocked_users?.length ?? 0;
}

export interface McpTenantList {
  /**
   * Optional, not because a real list ever omits it, but because
   * `client.ts`'s `as T` cast means nothing here is actually guaranteed — a
   * malformed or truncated response arrives typed as if it were valid. Read
   * through `tenantsOf`, never this field directly.
   */
  tenants?: McpTenantSummary[];
  count?: number;
  known_tiers?: string[];
  /**
   * Emails with a ("*", email) row — blocked EVERYWHERE.
   *
   * Reported once, separately, because it is not a property of any tenant in
   * the list. Attaching it per-merchant would misreport scope.
   */
  users_blocked_across_all_merchants?: string[];
  note?: string;
}

/** `data.tenants`, defaulting to empty rather than throwing on `.filter`/`.map`. */
export function tenantsOf(l: McpTenantList | undefined | null): McpTenantSummary[] {
  return l?.tenants ?? [];
}

/** Which blocklist rows matched. Up to three, each a different scope. */
export interface BlocklistMatch {
  merchant_wide?: BlocklistRow;
  user_everywhere?: BlocklistRow;
  user_in_merchant?: BlocklistRow;
}

export interface BlocklistRow {
  business_reference?: string;
  email?: string;
  reason?: string;
  added_by?: string;
  added_at?: string;
}

export interface McpTenantDetail {
  business_reference: string;
  business_name: string;
  email: string;
  /**
   * What is actually STORED — `null` when no CURRENT entitlements row exists.
   * A GET never creates one, so null is the honest answer rather than "free".
   */
  tier: string | null;
  /** Who last set it, e.g. "admin:someone@foodics.com". */
  tier_source?: string;
  tier_updated_at?: string;
  /** What the server actually applies: `tier ?? "free"`. */
  effective_tier: string;
  tier_is_implicit: boolean;
  /**
   * MERCHANT-WIDE block only — the ("<ref>", "*") row. Flat, replacing the old
   * `blocklist: {blocked, matched}` object: a merchant lookup only ever has
   * the merchant-wide scope to report, so `matched`'s three-way split (which
   * belongs to the per-USER route) never applied here and the nesting bought
   * nothing.
   */
  blocked: boolean;
  /** Present only when `blocked` is true. */
  blocked_since?: string;
  blocked_by?: string;
  blocked_reason?: string;
  /** Emails blocked at this merchant specifically — the per-user rows. */
  blocked_users?: string[];
}

export interface McpTierHistory {
  business_reference: string;
  history?: {
    tier: string;
    /**
     * "signup" (auto, first use) or "admin:{actor}". Never a bare "admin".
     * Nullable for the same reason `AuditEvent.actor` is: a row without one
     * fed a bare `.replace()` inside a `.map`, which is Incident 1's exact
     * shape one file over.
     */
    source: string | null;
    created_at: string | null;
  }[];
}

/** `data.history`, defaulting to `[]`. */
export function tierHistoryOf(h: McpTierHistory | undefined | null): NonNullable<McpTierHistory["history"]> {
  return h?.history ?? [];
}

// ------------------------------------------------------------------ usage --

export interface McpToolUsage {
  spent: number;
  cap: number;
  /** "unlimited" | "gated" | "metered (N/day)" — the API's own vocabulary. */
  mode: string;
  cap_source: string;
  /** True ONLY when cap > 0 and spent >= cap. See `usageStatus`. */
  exhausted: boolean;
}

/** The tier's `max_calls_per_day` as today's usage reports it. `cap` is null when no total is set. */
export interface McpDailyTotal {
  cap?: number | null;
  mode?: string;
  spent?: number;
  exhausted?: boolean;
}

export interface McpUsage {
  business_reference: string;
  tier: string;
  /** UTC calendar day. State the boundary in the UI — 02:00 in Riyadh is yesterday. */
  day: string;
  day_boundary: string;
  tools?: Record<string, McpToolUsage>;
  total_credits: number;
  /**
   * Calls across every tool. `spent` includes tools with no row in `tools`,
   * so it can be higher than `total_credits`. Absent on a truncated body —
   * read through `dailyTotalOf`.
   */
  daily_total?: McpDailyTotal | null;
  note?: string;
}

/** `data.tools`, defaulting to `{}`. */
export function toolUsageOf(u: McpUsage | undefined | null): Record<string, McpToolUsage> {
  return u?.tools ?? {};
}

/** `data.daily_total`, or null when it is missing or not an object. */
export function dailyTotalOf(u: McpUsage | undefined | null): McpDailyTotal | null {
  const d = u?.daily_total;
  if (!d || typeof d !== "object") return null;
  return d;
}

/**
 * The sentence for a daily total that outruns the per-tool credit sum.
 *
 * Null when there is nothing to correct: no total, or `spent` is not above
 * `total_credits`. The words are built from those two numbers so a page
 * cannot hard-code either.
 */
export function dailyTotalSentence(u: McpUsage | undefined | null): string | null {
  const spent = dailyTotalOf(u)?.spent;
  const credits = u?.total_credits;
  if (typeof spent !== "number" || typeof credits !== "number" || spent <= credits) return null;
  return `${spent} is higher than the ${credits} credits today. The daily total counts every metered call, including tools with no row here.`;
}

// ------------------------------------------------------------------ users --

export interface McpUserBlockState {
  business_reference: string;
  // The server calls this `user_id`, not `email` — it is the normalised email
  // value, just under that key (admin/app.py:308-311).
  user_id: string;
  blocked: boolean;
  matched?: BlocklistMatch;
}

// ---------------------------------------------------------------- catalog --

/**
 * `resolver_tools` and `resolver_report_dependents` are GONE — MCP removed the
 * resolver-cap guard entirely (its own Postman collection now warns "NO 409
 * WILL STOP YOU"). There is no live data to build a resolver warning from;
 * see `RESOLVER_GUARD_REMOVED` for where the warning lives instead.
 */
export interface McpCatalog {
  tools?: string[];
  note?: string;
}

/** `data.tools`, defaulting to `[]`. */
export function toolsOf(c: McpCatalog | undefined | null): string[] {
  return c?.tools ?? [];
}

/**
 * MCP dropped the resolver-cap guard (commit `0bf50a1`) — capping a
 * `search_*` tool now succeeds unconditionally. This is the one place that
 * fact is said aloud; every call site that used to render a resolver warning
 * points here instead of reconstructing its own copy.
 */
export const RESOLVER_GUARD_REMOVED =
  "MCP no longer refuses this. Capping a search_* tool can strand every " +
  "report that resolves IDs through it — the guard that used to catch that " +
  "before the write landed has been removed server-side, on dev and prod " +
  "alike. Nothing here will stop you either.";

export interface McpTiers {
  /** Optional for the usual reason — `client.ts` validates nothing. Read it
   *  through `tiersOf`, never directly: spreading an absent array throws
   *  "known is not iterable" and takes the page down. */
  tiers?: string[];
  note?: string;
}

/** `data.tiers`, defaulting to `[]`. */
export function tiersOf(t: McpTiers | undefined | null): string[] {
  return t?.tiers ?? [];
}

// ------------------------------------------------------------------ quota --

/**
 * One tier's quota document, as stored. Every key is optional and an absent
 * one means something specific (docs/products/mcp.md, "Quota"):
 * `max_calls_per_day` absent = no daily total, `tool_default` absent =
 * unlimited, `ratelimit.{lane}` absent = the server's default. `tools` values
 * are `-1` unlimited, `0` gated, `N > 0` N a day.
 *
 * Read `tools` through `toolCapsOf`, never directly.
 */
export interface McpQuotaDoc {
  max_calls_per_day?: number | null;
  tool_default?: number | null;
  tools?: Record<string, number | null>;
  ratelimit?: Partial<Record<RateLimitLane, McpRateLimitLaneValue | null>>;
}

/** `GET /admin/quota`. */
export interface McpQuotaAll {
  /**
   * tier → that tier's document, as stored. Optional per the `client.ts` cast
   * note on `McpTenantList.tenants` — read through `tierDocsOf`. This replaced
   * a `caps` map (tier → tool → cap, `"*"` for the default) that the console
   * kept reading after MCP stopped sending it, which left every cap card empty.
   */
  tiers?: Record<string, McpQuotaDoc>;
  modes?: Record<string, string>;
  /** What each document key means, in MCP's words. */
  fields?: Record<string, string>;
  /** ~300. The cache TTL — never say "instant" in the UI. */
  effective_within_seconds?: number;
  note?: string;
}

/** `data.tiers`, defaulting to `{}`. */
export function tierDocsOf(q: McpQuotaAll | undefined | null): Record<string, McpQuotaDoc> {
  const tiers = q?.tiers;
  return tiers && typeof tiers === "object" ? tiers : {};
}

/**
 * A document's own per-tool caps, defaulting to `{}`. Only numbers: a `null`
 * entry is how a PATCH removes a cap and is never a stored value.
 */
export function toolCapsOf(doc: McpQuotaDoc | undefined | null): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [tool, cap] of Object.entries(doc?.tools ?? {})) {
    if (typeof cap === "number") out[tool] = cap;
  }
  return out;
}

/**
 * Stored cap names that `GET /admin/catalog` does not list.
 *
 * An absent `tools` array is not "the catalog is empty" — a truncated body
 * would otherwise offer to null every cap. Names are sorted so the one PATCH
 * that clears them is stable.
 */
export function retiredToolNames(
  caps: Record<string, number>,
  catalog: McpCatalog | null | undefined,
): string[] {
  if (!catalog || !Array.isArray(catalog.tools)) return [];
  const known = new Set(catalog.tools);
  return Object.keys(caps)
    .filter((name) => !known.has(name))
    .sort((a, b) => a.localeCompare(b));
}

/** One lane's rate, as both the stored document and `effective` carry it. */
export interface McpRateLimitLaneValue {
  max_requests?: number;
  per_seconds?: number;
  /** `effective` only: `tier` or `code default`. Absent on the stored document. */
  source?: string;
}

/**
 * One tier's quota document.
 *
 * `ratelimit` LIVES HERE NOW. It used to be four flat SSM keys written by
 * `PATCH /admin/ratelimit/{lane}`, which MCP retired — rate limits are part
 * of the tier, set with the caps, and `effective` fills in the code defaults
 * so a panel can say whether a lane is the tier's own or inherited.
 *
 * Note `per_seconds`, NOT `window_seconds`: the old lane route's spelling.
 */
export interface McpQuotaTier {
  tier: string;
  /** `document`, `legacy rows` or `none`. */
  source?: string;
  /** Null when there is no document. Writes are optimistic on it. */
  version?: number | null;
  /** The stored document. `{}` for a tier with none — this route does not 404. */
  config?: McpQuotaDoc;
  effective?: {
    max_calls_per_day?: { value?: number; mode?: string };
    tool_default?: { value?: number; mode?: string };
    ratelimit?: Partial<Record<RateLimitLane, McpRateLimitLaneValue>>;
  };
  effective_within_seconds?: number;
  legacy_rows_ignored?: unknown;
}

/**
 * Legacy row names still sitting beside a document.
 *
 * The server sends a list of strings (the old sort keys). Anything else —
 * a missing field, a DynamoDB attribute wrapper, an object in the list —
 * is dropped. Rendering an object is React's "objects are not valid as a
 * React child".
 */
export function legacyRowsOf(q: McpQuotaTier | undefined | null): string[] {
  const rows = q?.legacy_rows_ignored;
  if (!Array.isArray(rows)) return [];
  return rows.filter((row): row is string => typeof row === "string");
}

/** The header line for where this tier's quota came from. */
export function documentFactOf(q: McpQuotaTier | undefined | null): string {
  if (!q) return "No document yet.";
  if (typeof q.version === "number") return `Stored as ${q.source ?? "—"}, version ${q.version}.`;
  if (q.source && q.source !== "none") return `Stored as ${q.source}.`;
  return "No document yet.";
}

/**
 * One lane's effective rate for a tier, or null when it cannot be read.
 *
 * Reads `effective`, not `config`: a tier with no stored lane still HAS a
 * rate — the server's code default — and rendering the stored document alone
 * would show an empty field where a real limit applies.
 */
export function tierRateLimitOf(
  q: McpQuotaTier | undefined | null,
  lane: RateLimitLane,
): McpRateLimitLaneValue | null {
  return q?.effective?.ratelimit?.[lane] ?? null;
}

/**
 * A tier-wide limit as the server applies it: `effective`, falling back to the
 * stored document, falling back to unlimited (`-1`) — which is what an absent
 * `tool_default` or `max_calls_per_day` means.
 */
/**
 * `tool_count` on a 409 from setting `tool_default` or `max_calls_per_day`
 * to 0. Absent on the concurrent-write 409, which is why the two must not
 * share a button: only this one is overridable with `?force=true`.
 */
export function toolCountOf(body: unknown): number | undefined {
  const n = (body as { tool_count?: unknown } | null | undefined)?.tool_count;
  return typeof n === "number" ? n : undefined;
}

export function tierLimitOf(
  q: McpQuotaTier | undefined | null,
  key: "tool_default" | "max_calls_per_day",
): number {
  const effective = q?.effective?.[key]?.value;
  if (typeof effective === "number") return effective;
  const stored = q?.config?.[key];
  return typeof stored === "number" ? stored : -1;
}

// --------------------------------------------------------------- settings --

export interface McpSettingParam {
  key: string;
  value: string | null;
  /** False means never seeded — the code default applies. Not an error. */
  exists: boolean;
  writable: boolean;
  /**
   * When a change reaches running tasks. Differs per key. Render it through
   * `settingEffectText` (components/settings/settingsCopy.ts), which maps the
   * known shapes to plain words and falls back to this raw string.
   */
  effect: string;
  /**
   * What the server runs on while the key is unset (MCP #337): the text or
   * value from MCP's `common/defaults.py`, or `null` for a key that is not
   * admin-owned. `unknown` because a tuple default arrives as a list, and an
   * older MCP sends no field at all. Read through `settingDefaultText`.
   */
  default?: unknown;
}

export interface McpSettings {
  parameters?: McpSettingParam[];
  writable?: string[];
  note?: string;
}

/**
 * `data.writable`, defaulting to `[]`.
 *
 * `new Set(s.writable)` on an absent field is legal JS and silently produces
 * an EMPTY set — every writable key would then render as read-only, with no
 * error anywhere. This is the guarded version; nothing should call
 * `new Set(s.writable)` directly.
 */
export function writableKeysOf(s: McpSettings | undefined | null): string[] {
  return s?.writable ?? [];
}

/** `data.parameters`, defaulting to `[]`. */
export function settingsParamsOf(s: McpSettings | undefined | null): McpSettingParam[] {
  return s?.parameters ?? [];
}

/**
 * A settings entry's `default`, as text to show greyed under an unset key — or
 * null when there is nothing true to show (an older MCP without the field, a
 * key that is not admin-owned, or a shape this console cannot render as text).
 */
export function settingDefaultText(p: { default?: unknown } | undefined | null): string | null {
  const d = p?.default;
  if (typeof d === "string") return d === "" ? null : d;
  if (typeof d === "number" || typeof d === "boolean") return String(d);
  if (Array.isArray(d) && d.every((x) => typeof x === "string" || typeof x === "number")) {
    return d.join(", ");
  }
  return null;
}

// ------------------------------------------------------- rate limits -------

/**
 * The two lanes a tier document's `ratelimit` takes. Anything else 422s
 * naming this exact set.
 */
export const RATE_LIMIT_LANES = ["core", "reports"] as const;
export type RateLimitLane = (typeof RATE_LIMIT_LANES)[number];

/**
 * The SSM key each lane's ceiling lives under — read-only everywhere,
 * including here. `GET /admin/settings` returns both as ordinary read-only
 * `parameters` rows; there is no dedicated ceiling route.
 */
export const RATE_LIMIT_CEILING_KEY: Record<RateLimitLane, string> = {
  core: "ratelimit/foodics_core_ceiling_per_min",
  reports: "ratelimit/foodics_reports_ceiling_per_hour",
};

/** The window the ceiling itself is expressed over — 60s for core, 1h for reports. */
export const RATE_LIMIT_CEILING_WINDOW_SECONDS: Record<RateLimitLane, number> = {
  core: 60,
  reports: 3600,
};

/** A lane's live ceiling, read off the same settings parameters list. `null` if unseeded. */
export function rateLimitCeilingOf(params: McpSettingParam[], lane: RateLimitLane): number | null {
  const value = params.find((p) => p.key === RATE_LIMIT_CEILING_KEY[lane])?.value ?? null;
  return value !== null ? Number(value) : null;
}

/**
 * The same normalization the server applies, so a client-side preview can
 * show "normalizes to X per Y" before submit without pretending to be the
 * real gate — the 422 is still the only actual enforcement.
 */
export function normalizedRate(
  maxRequests: number,
  windowSeconds: number,
  lane: RateLimitLane,
): number {
  return (maxRequests / windowSeconds) * RATE_LIMIT_CEILING_WINDOW_SECONDS[lane];
}

/**
 * The ceiling 422's own numbers.
 *
 * The preview above the form is the client's arithmetic. This is what the
 * server refused with, and the two can disagree. Null unless `lane`,
 * `ceiling` and `normalized_rate` are all present — a Labeeb 422 carries
 * `detail.fields` instead and must not be read as one of these.
 */
export function ceilingRefusalOf(body: unknown): {
  lane: string;
  ceiling: number;
  windowSeconds: number | null;
  normalizedRate: number;
} | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  if (typeof b.lane !== "string") return null;
  if (typeof b.ceiling !== "number") return null;
  if (typeof b.normalized_rate !== "number") return null;
  return {
    lane: b.lane,
    ceiling: b.ceiling,
    windowSeconds: typeof b.ceiling_window_seconds === "number" ? b.ceiling_window_seconds : null,
    normalizedRate: b.normalized_rate,
  };
}

// ------------------------------------------------------- derived helpers ---

/**
 * A cap's tone. An alias, not a second definition — see components/tone.ts.
 * The domain name is kept because `capLabel` returns a cap's tone, and a
 * reader of that signature should not have to know it is the shared union.
 */
export type CapTone = Tone;

/** -1 unlimited · 0 gated · N metered. Rendering the raw number misleads. */
export function capLabel(n: number): { text: string; tone: CapTone } {
  if (n < 0) return { text: "Unlimited", tone: "green" };
  if (n === 0) return { text: "Gated", tone: "red" };
  return { text: `${n}/day`, tone: "neutral" };
}

export type UsageStatus =
  | { kind: "gated"; text: string; tone: CapTone }
  | { kind: "exhausted"; text: string; tone: CapTone }
  | { kind: "metered"; text: string; tone: CapTone }
  | { kind: "unlimited"; text: string; tone: CapTone };

/**
 * One status per tool, computed — because `exhausted` lies.
 *
 * The API sets `exhausted` true only when `cap > 0 && spent >= cap`, so a
 * GATED tool (cap 0, never allowed) reports `exhausted: false` despite being
 * strictly more restricted than an exhausted one. Binding that field directly
 * to an "Exhausted" column tells the operator the opposite of the truth, so
 * there is no such column — this function is the only reader.
 */
export function usageStatus(u: McpToolUsage): UsageStatus {
  if (u.cap === 0) {
    return { kind: "gated", text: "Gated — never allowed", tone: "red" };
  }
  if (u.cap < 0) {
    return { kind: "unlimited", text: `Unlimited · ${u.spent} used`, tone: "green" };
  }
  if (u.exhausted || u.spent >= u.cap) {
    return { kind: "exhausted", text: `Exhausted · ${u.spent} of ${u.cap}`, tone: "amber" };
  }
  return { kind: "metered", text: `${u.spent} of ${u.cap}`, tone: "neutral" };
}

export interface CapChange {
  /** A tool name, or `max_calls_per_day` / `tool_default` for the tier-wide limits. */
  tool: string;
  from: number;
  to: number;
}

/**
 * What changes for a merchant moving between tiers.
 *
 * Turns an opaque label swap into a statement of what they will experience.
 * Compares two tiers' documents as the server resolves them: an absent
 * `max_calls_per_day` or `tool_default` is unlimited (`-1`), and a tool absent
 * from a tier's `tools` inherits that tier's `tool_default` — so absence is
 * never read as "no change". The two tier-wide limits come first: they have
 * the widest effect.
 */
export function capDiff(from: McpQuotaDoc, to: McpQuotaDoc): CapChange[] {
  const changes: CapChange[] = [];
  const fromTotal = from.max_calls_per_day ?? -1;
  const toTotal = to.max_calls_per_day ?? -1;
  if (fromTotal !== toTotal) changes.push({ tool: "max_calls_per_day", from: fromTotal, to: toTotal });

  const fromDefault = from.tool_default ?? -1;
  const toDefault = to.tool_default ?? -1;
  if (fromDefault !== toDefault) changes.push({ tool: "tool_default", from: fromDefault, to: toDefault });

  const fromTools = toolCapsOf(from);
  const toTools = toolCapsOf(to);
  const tools = new Set([...Object.keys(fromTools), ...Object.keys(toTools)]);
  for (const tool of [...tools].sort()) {
    const a = fromTools[tool] ?? fromDefault;
    const b = toTools[tool] ?? toDefault;
    if (a !== b) changes.push({ tool, from: a, to: b });
  }
  return changes;
}

/**
 * The sentinel for "no entitlements row", used by tier filters in the UI.
 *
 * DELIBERATELY NOT the string "none", and this is the subtlest trap in the
 * whole API:
 *
 *   - `GET /admin/tenants?tier=` is an EXACT MATCH on the stored value. A
 *     tier-less merchant stores `null`, and no merchant ever stores the string
 *     "none" — so `?tier=none` returns an empty list, silently, and reads as
 *     "we have no such merchants" rather than "that filter cannot express it".
 *   - `GET /admin/metrics/tier_distribution` buckets those merchants under
 *     the string key `"none"`, because a JSON object needs a string key and
 *     `null` cannot be one.
 *
 * The two look inconsistent side by side and neither is wrong. The danger is
 * wiring a distribution bucket straight into a list filter — the "none" bucket
 * would show a count and then drill through to nothing.
 *
 * So: this sentinel never goes on the wire. `matchesTier` resolves it
 * client-side, and `tierQuery` returns undefined for it rather than sending a
 * value the server would match against literally.
 */
export const TIER_UNSET = "\u0000unset";

/** The `tier_distribution` key for merchants with no entitlements row. */
export const TIER_DISTRIBUTION_UNSET_KEY = "none";

/** Does this merchant match a tier filter? `TIER_UNSET` means "no row stored". */
export function matchesTier(t: { tier: string | null }, filter: string): boolean {
  if (!filter) return true;
  if (filter === TIER_UNSET) return t.tier === null;
  return t.tier === filter;
}

/**
 * The `?tier=` value to send, or undefined when the filter cannot be expressed
 * server-side. Callers must filter client-side when this returns undefined for
 * a non-empty filter — see TIER_UNSET.
 */
export function tierQuery(filter: string): string | undefined {
  if (!filter || filter === TIER_UNSET) return undefined;
  return filter;
}

/** `GET /mcp/admin/tenants`, with `?tier=` only when the server can express it. */
export function tenantsListPath(tierFilter: string): string {
  const t = tierQuery(tierFilter);
  if (!t) return "/mcp/admin/tenants";
  return `/mcp/admin/tenants?tier=${encodeURIComponent(t)}`;
}

// ---------------------------------------------------------------- audit ----

/** The domains the audit table keys on. Anything else is a 422. `mcp_clients` is the
 *  AI-app allowlist (add/remove an app; `before`/`after` are the URI LIST).
 *  `availability` is the paused-tools list (`set_paused_tools`, target
 *  `paused_tools`; `before`/`after` are the WHOLE paused list). `waf` is the
 *  blocked-IP list (`block_ip` / `unblock_ip`, target the CIDR; `before`/`after`
 *  are that IP version's whole list). */
export const AUDIT_DOMAINS = ["tenant", "quota", "settings", "mcp_clients", "availability", "waf"] as const;
export type AuditDomain = (typeof AUDIT_DOMAINS)[number];

/**
 * Fleet or per-domain audit list. Cursor is opaque — pass it via the client's
 * `query` option so a second `?` cannot wipe `days`/`limit`. Domain lists are
 * not day-windowed.
 */
export function auditListPath(domain: AuditDomain | "", days = 7, limit = 50): string {
  const base = domain ? `/mcp/admin/audit/${domain}` : "/mcp/admin/audit";
  const q = new URLSearchParams();
  // Only the fleet list is day-windowed. The server clamps 1–90; do it here
  // too so the sentence on the page and the query cannot disagree.
  if (!domain) q.set("days", String(auditWindowDays(days)));
  // The server clamps 1–200.
  q.set("limit", String(Math.min(200, Math.max(1, Math.floor(limit)))));
  return `${base}?${q.toString()}`;
}

/** `?days=` on the fleet audit list: an integer from 1 to 90, default 7. */
export function auditWindowDays(days: number): number {
  if (!Number.isInteger(days)) return 7;
  return Math.min(90, Math.max(1, days));
}

/**
 * `PATCH /admin/quota/{tier}` — the response to a partial update of the
 * tier's document. Optional throughout, per the `client.ts` cast note.
 */
export interface McpQuotaPatch {
  tier?: string;
  /** The document before this write. */
  before?: McpQuotaDoc;
  /** The whole document after it. */
  config?: McpQuotaDoc;
  version?: number;
  migrated_from_legacy_rows?: unknown[];
  /** One entry per lane in the resulting document: how far under Foodics' ceiling it is. */
  ratelimit_checks?: Partial<
    Record<RateLimitLane, { normalized_rate?: number; ceiling?: number; ceiling_window_seconds?: number }>
  >;
  effective?: McpQuotaTier["effective"];
  /** ~300. The cache TTL — never say "instant". */
  effective_within_seconds?: number;
}

/**
 * `DELETE /admin/quota/{tier}/{tool}` — removes one tool's own cap from the
 * tier's `tools`. No `force`, no body; a 404 means the tier has no cap of its
 * own for that tool.
 *
 * `now_resolves_to` is what the tool ACTUALLY falls back to — the tier's
 * `tool_default`, or unlimited. The success message is computed from it, never
 * from the tier's document, as the doc asks.
 */
export interface McpQuotaDelete {
  tier?: string;
  tool?: string;
  deleted?: boolean;
  previous?: number;
  now_resolves_to?: number;
  /** ~300. The cache TTL — never say "instant". */
  effective_within_seconds?: number;
}

/**
 * One admin action. NOT a merchant tool call — a different table answering a
 * different question, and the API reference is explicit that the two must
 * never share a panel.
 */
export interface AuditEvent {
  domain: AuditDomain;
  /** e.g. "set_tier", "block", "set_cap". */
  action: string;
  /**
   * `admin:{email}` — from the verified JWT since dual-auth shipped.
   *
   * NULLABLE, like `target` and `timestamp` below, and none of the three is a
   * hypothetical. The reference documents all three as always present; dev's
   * table holds at least one row without a `timestamp`, and reading it as a
   * string threw inside the row map — which in React 19 unmounts the whole
   * root, so ONE legacy row blanked the entire console, shell included.
   *
   * They are typed as nullable so a bare `.replace`/`.split`/`.startsWith` on
   * any of them is a compile error rather than a white screen. An audit log is
   * the last surface that should fall over: it is what you open when something
   * has already gone wrong.
   */
  actor: string | null;
  /**
   * What changed: a merchant reference, a tier, an SSM key. May hold a slash.
   *
   * `unknown`, not `string | null`, for the same reason `before`/`after` are.
   * `admin/services/audit.py`'s `_deserialize` reads `attr.get("S")` and, for
   * any attribute that is NOT a DynamoDB string, assigns the raw attribute
   * WRAPPER instead — `{"N": "5"}`, `{"BOOL": true}` — for every key on the
   * row, this one included. Today's writer only ever writes strings, but a
   * legacy row is precisely what already happened with `timestamp`, and this
   * field feeds `encodeURIComponent` and `.split("/")` as well as JSX. Read
   * it through `auditTarget()`.
   */
  target: unknown;
  /**
   * Whatever JSON value the action wrote — NOT always a string.
   *
   * The server stores every value as a DynamoDB string, JSON-encoding
   * anything that is not one already, then decodes it back on read
   * (admin/audit_log.py, admin/services/audit.py). What comes out the other
   * side is action-shaped: a boolean for block/unblock, a string for
   * set_tier, a `{tool: cap}` dict for quota's set_caps. Rendering either
   * field directly as a JSX child is exactly React error #31 the moment a
   * quota row shows up — use `auditValue()` below, never `{e.before}`.
   */
  before: unknown;
  after: unknown;
  timestamp: string | null;
  /**
   * Why the operator said they made this change.
   *
   * THE CONSOLE COLLECTED THIS AND NEVER SHOWED IT. Every L2 write takes a
   * reason through `ConfirmDialog` (min 10 chars), sends it, and MCP stores
   * it on the row — but this interface did not declare the field, so the
   * audit log rendered actor/action/target and silently dropped the one
   * part a reader six months later actually needs.
   *
   * Optional because it is only there when the write sent one: older rows,
   * and the few writes that take no reason, have none.
   */
  reason?: string | null;
  request_id?: string;
  /**
   * The row's sort key, `<iso>#<action>#<uuid8>` — unique per row, so it is
   * how a row two reads both returned is shown once. `unknown` for the same
   * wrapper reason as `target`; read it through `auditTarget()`.
   */
  sk?: unknown;
}

/**
 * `before`/`after` as one line of text, whatever JSON type they decoded to.
 *
 * A string renders as itself; anything else (a bool, a dict, a number) is
 * stringified rather than handed to JSX, which is the only thing standing
 * between a quota row's `{tool: cap}` object and React error #31.
 */
/**
 * An audit row's `target` as a string, or null when it is not usable as one.
 *
 * Null rather than a marker string, because callers do two different things
 * with it: build a URL (`encodeURIComponent`, `.split("/")`) and render it.
 * A caller that must not construct `/tenants/[object%20Object]` needs to know
 * the value was unusable, which a "not recorded" string would hide.
 */
export function auditTarget(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

function isAuditRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/** Human label for `enabled_domains` on a settings document. */
export function formatEnabledDomains(v: unknown): string {
  if (v === "all") return "all domains";
  if (Array.isArray(v)) return v.length === 0 ? "no domains" : v.join(", ");
  return auditValue(v);
}

function settingsBody(v: unknown): Record<string, unknown> | null {
  if (!isAuditRecord(v)) return null;
  if (isAuditRecord(v.settings)) return v.settings;
  if ("enabled_domains" in v || "disabled_domains" in v || "disabled_plugins" in v) return v;
  return null;
}

function summarizeAuditSide(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (v === "all" || Array.isArray(v)) return formatEnabledDomains(v);
  if (typeof v === "boolean") return v ? "blocked" : "not blocked";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);

  const settings = settingsBody(v);
  if (settings) {
    const parts: string[] = [];
    if ("enabled_domains" in settings) {
      parts.push(formatEnabledDomains(settings.enabled_domains));
    }
    if (Array.isArray(settings.disabled_domains) && settings.disabled_domains.length > 0) {
      parts.push(`domains off: ${settings.disabled_domains.join(", ")}`);
    }
    if (Array.isArray(settings.disabled_plugins) && settings.disabled_plugins.length > 0) {
      parts.push(`plugins off: ${settings.disabled_plugins.join(", ")}`);
    }
    if (parts.length > 0) return parts.join("; ");
  }

  if (isAuditRecord(v)) {
    if (typeof v.status === "string") return v.status;
    if (typeof v.tier_id === "string") {
      const daily = v.daily;
      const monthly = v.monthly;
      if (typeof daily === "number" && typeof monthly === "number") {
        const d = daily < 0 ? "∞" : String(daily);
        const m = monthly < 0 ? "∞" : String(monthly);
        return `${v.tier_id} (${d}/${m} per day/month)`;
      }
      return v.tier_id;
    }
    if ("daily" in v && "monthly" in v && !("tier_id" in v)) {
      const daily = v.daily;
      const monthly = v.monthly;
      if (typeof daily === "number" && typeof monthly === "number") {
        const d = daily < 0 ? "∞" : String(daily);
        const m = monthly < 0 ? "∞" : String(monthly);
        return `${d}/${m} per day/month`;
      }
    }
    const capKeys = Object.keys(v).filter((k) => typeof v[k] === "number");
    if (capKeys.length > 0 && capKeys.length <= 8) {
      return capKeys
        .sort()
        .map((k) => {
          const n = v[k];
          return `${k}: ${typeof n === "number" && n < 0 ? "∞" : String(n)}`;
        })
        .join(", ");
    }
  }

  return auditValue(v);
}

/**
 * Before/after text for an audit row — short labels instead of full JSON
 * documents when the shape is one the admin APIs use often.
 */
export function auditChangePair(
  before: unknown,
  after: unknown,
  action?: string | null,
): { label: string | null; before: string; after: string } {
  const bSettings = settingsBody(before);
  const aSettings = settingsBody(after);
  const settingsAction =
    action?.includes("settings") || action === "settings-set" || action === "settings.patch";

  if (
    settingsAction ||
    bSettings?.enabled_domains !== undefined ||
    aSettings?.enabled_domains !== undefined
  ) {
    const beforeStr = bSettings
      ? formatEnabledDomains(bSettings.enabled_domains)
      : summarizeAuditSide(before);
    const afterStr = aSettings
      ? formatEnabledDomains(aSettings.enabled_domains)
      : summarizeAuditSide(after);
    if (beforeStr === "—" && afterStr === "—") {
      return { label: null, before: "—", after: "—" };
    }
    return {
      label: "Enabled domains",
      before: beforeStr,
      after: afterStr,
    };
  }

  if (
    bSettings?.disabled_domains !== undefined ||
    aSettings?.disabled_domains !== undefined ||
    bSettings?.disabled_plugins !== undefined ||
    aSettings?.disabled_plugins !== undefined
  ) {
    return {
      label: "Environment",
      before: summarizeAuditSide(before),
      after: summarizeAuditSide(after),
    };
  }

  if (action?.includes("standing") || action?.includes("block") || action === "block") {
    return {
      label: "Standing",
      before: summarizeAuditSide(before),
      after: summarizeAuditSide(after),
    };
  }

  if (action?.includes("quota") || action?.includes("tier") || action === "set_tier") {
    return {
      label: "Tier / quota",
      before: summarizeAuditSide(before),
      after: summarizeAuditSide(after),
    };
  }

  if (action === "set_caps") {
    return {
      label: "Caps",
      before: summarizeAuditSide(before),
      after: summarizeAuditSide(after),
    };
  }

  return {
    label: null,
    before: summarizeAuditSide(before),
    after: summarizeAuditSide(after),
  };
}

/** Document metadata — not what the operator changed. */
const AUDIT_META_KEYS = new Set([
  "schema_version",
  "model_version",
  "by",
  "at",
  "reason",
  "version",
  "tenant_id",
  "api_version",
  "change_id",
  "origin",
  "request_id",
  "authority",
]);

function formatAuditFieldValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (v === "all" || Array.isArray(v)) return formatEnabledDomains(v);
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (isAuditRecord(v)) {
    const short = summarizeAuditSide(v);
    if (short.length <= 120 && !short.startsWith("{")) return short;
  }
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/** Flatten an audit payload to comparable keys (settings.* and top-level fields). */
/** Some rows store JSON documents as strings; parse before diffing. */
function parseAuditPayload(v: unknown): unknown {
  if (typeof v !== "string") return v;
  const s = v.trim();
  if (!s.startsWith("{") && !s.startsWith("[")) return v;
  try {
    return JSON.parse(s) as unknown;
  } catch {
    return v;
  }
}

function flattenAuditPayload(v: unknown, prefix = ""): Record<string, unknown> {
  v = parseAuditPayload(v);
  if (v === null || v === undefined) return {};
  if (!isAuditRecord(v)) {
    const key = prefix || "value";
    return { [key]: v };
  }

  const out: Record<string, unknown> = {};
  if (isAuditRecord(v.settings)) {
    for (const [k, val] of Object.entries(v.settings)) {
      out[prefix ? `${prefix}.settings.${k}` : `settings.${k}`] = val;
    }
  }

  for (const [k, val] of Object.entries(v)) {
    if (k === "settings" || AUDIT_META_KEYS.has(k)) continue;
    const path = prefix ? `${prefix}.${k}` : k;
    if (isAuditRecord(val) && !Array.isArray(val)) {
      Object.assign(out, flattenAuditPayload(val, path));
    } else {
      out[path] = val;
    }
  }
  return out;
}

/**
 * Rows for a before/after table: only keys whose formatted value changed.
 * Falls back to one row from `auditChangePair` when nothing flattens cleanly.
 */
export function auditChangeFields(
  before: unknown,
  after: unknown,
  action?: string | null,
): { key: string; before: string; after: string }[] {
  // A paused-tools row's before/after are the whole list. The generic path
  // would render `[]` as Labeeb's "no domains", which is a different product's
  // sentence and the wrong one.
  if (action === PAUSED_TOOLS_ACTION) {
    return [
      {
        key: "paused",
        before: pausedListText(parseAuditPayload(before)),
        after: pausedListText(parseAuditPayload(after)),
      },
    ];
  }
  const b = flattenAuditPayload(parseAuditPayload(before));
  const a = flattenAuditPayload(parseAuditPayload(after));
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].sort();
  const rows = keys
    .map((key) => ({
      key,
      before: formatAuditFieldValue(b[key]),
      after: formatAuditFieldValue(a[key]),
    }))
    .filter((row) => row.before !== row.after);

  if (rows.length > 0) return rows;

  const pair = auditChangePair(before, after, action);
  if (pair.before === "—" && pair.after === "—") return [];
  return [
    {
      key: pair.label ?? "value",
      before: pair.before,
      after: pair.after,
    },
  ];
}

export function auditValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "boolean" || typeof v === "number") return String(v);
  // A flat list (the AI-app allowlist, the paused-tools list) reads as a
  // list; anything nested is still JSON below.
  if (Array.isArray(v) && v.every((x) => typeof x === "string" || typeof x === "number")) {
    return v.length === 0 ? "none" : v.join(", ");
  }
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export interface AuditPage {
  events?: AuditEvent[];
  /**
   * Opaque. Pass it back verbatim as `?cursor=`; never construct or parse one
   * — it encodes a DynamoDB pagination key. Null when there is no more.
   */
  cursor?: string | null;
  count?: number;
}

/**
 * `data.events`, defaulting to `[]`.
 *
 * `useAuditFeed`'s page-append does `[...prev.data.events, ...page.events]`
 * inside a `setState` updater — a throw there is worse than one during render,
 * because it fires outside the commit path with a stack trace that does not
 * name the component. Always read through this, including inside that spread.
 */
export function eventsOf(p: AuditPage | undefined | null): AuditEvent[] {
  return p?.events ?? [];
}

/** `admin:someone@foodics.com` → `someone@foodics.com`. Total, by design. */
export function actorEmail(actor: string | null | undefined): string {
  if (!actor) return "unknown actor";
  return actor.startsWith("admin:") ? actor.slice("admin:".length) : actor;
}

/**
 * An audit row's UTC stamp, rendered.
 *
 * `dateOnly` gives `2026-09-02`; otherwise `2026-09-02 08:41:00 UTC`. Returns
 * a marker rather than throwing when the row carries no stamp — see the note
 * on `AuditEvent.actor` for why that is not theoretical.
 */
export function auditStamp(
  timestamp: string | null | undefined,
  opts: { dateOnly?: boolean } = {},
): string {
  if (!timestamp) return "no timestamp";
  return opts.dateOnly
    ? timestamp.slice(0, 10)
    : `${timestamp.replace("T", " ").slice(0, 19)} UTC`;
}

// ------------------------------------------------------- quota audit rows --
//
// MCP audits the quota domain three ways (admin/routes/quotas.py):
//
//   set_tier_config  target `<tier>`         before/after: the whole tier documents
//   delete_cap       target `<tier>/<tool>`  before: the removed cap; no after
//   set_caps         target `<tier>`         legacy rows: `{tool: cap}` maps, with
//                                            `*`/`default` for the tier default and
//                                            `daily_total` for the daily total
//
// Read through `quotaAuditChange`, which says each row in words — never the
// action name, never JSON, never `null`.

/**
 * The two tier-wide limits of a quota document, in the operator's words.
 * Their keys are the API's names; printed raw they read like a tool that
 * failed to render (DA-3580). The tier-change diff and a quota audit row both
 * label them from here.
 */
export const TIER_WIDE_LABEL: Record<string, string> = {
  tool_default: "Every other tool (tier default)",
  max_calls_per_day: "Daily total, all tools",
};

/**
 * One DynamoDB attribute wrapper taken off, at any depth: `{"N": "5"}` → 5.
 *
 * `_deserialize` (admin/services/audit.py) passes any attribute that is not a
 * string through raw, so a legacy row's value can arrive as `{"N": "5"}` or a
 * `{"M": {…}}` of them — see `AuditEvent.target`. Only a one-key object whose
 * key and value match a wrapper is unwrapped; anything else keeps its shape.
 */
function unwrapDynamo(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(unwrapDynamo);
  if (!isAuditRecord(v)) return v;
  const keys = Object.keys(v);
  if (keys.length === 1) {
    const k = keys[0]!;
    const x = v[k];
    if (k === "S" && typeof x === "string") return x;
    if (k === "N" && typeof x === "string" && x.trim() !== "" && Number.isFinite(Number(x))) {
      return Number(x);
    }
    if (k === "BOOL" && typeof x === "boolean") return x;
    if (k === "NULL" && x === true) return null;
    if (k === "M" && isAuditRecord(x)) return unwrapDynamo(x);
    if (k === "L" && Array.isArray(x)) return x.map(unwrapDynamo);
  }
  return Object.fromEntries(Object.entries(v).map(([key, val]) => [key, unwrapDynamo(val)]));
}

/** An audit `before`/`after` as plain JSON: a JSON string decoded, wrappers taken off. */
export function auditPlain(v: unknown): unknown {
  return unwrapDynamo(parseAuditPayload(v));
}

/** A cap in words: `Unlimited`, `Gated`, `Metered 30/day`. */
export function capText(n: number): string {
  return n > 0 ? `Metered ${capLabel(n).text}` : capLabel(n).text;
}

function capNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && /^-?\d+$/.test(v.trim())) return Number(v);
  return undefined;
}

/** One side of a quota audit row, whichever of the two shapes it was written in. */
interface QuotaSide {
  /** A tier document. In one, an absent `tool_default` means unlimited. */
  doc: boolean;
  tool_default?: number;
  max_calls_per_day?: number;
  tools: Record<string, number>;
  ratelimit: Record<string, { max_requests?: number; per_seconds?: number }>;
}

const DOC_KEYS = ["tool_default", "max_calls_per_day", "tools", "ratelimit"];
const LEGACY_DEFAULT_KEYS = new Set(["*", "default"]);

/**
 * `isDoc` for a `set_tier_config` side, which is always a tier document —
 * including a new tier's `{}`, which has none of the document's keys to tell
 * it from an empty legacy map.
 */
function quotaSide(v: unknown, isDoc = false): QuotaSide {
  const plain = auditPlain(v);
  const side: QuotaSide = { doc: isDoc, tools: {}, ratelimit: {} };
  if (!isAuditRecord(plain)) return side;

  if (isDoc || DOC_KEYS.some((k) => k in plain)) {
    side.doc = true;
    side.tool_default = capNumber(plain.tool_default);
    side.max_calls_per_day = capNumber(plain.max_calls_per_day);
    if (isAuditRecord(plain.tools)) {
      for (const [tool, cap] of Object.entries(plain.tools)) {
        const n = capNumber(cap);
        if (n !== undefined) side.tools[tool] = n;
      }
    }
    if (isAuditRecord(plain.ratelimit)) {
      for (const [lane, spec] of Object.entries(plain.ratelimit)) {
        if (!isAuditRecord(spec)) continue;
        side.ratelimit[lane] = {
          max_requests: capNumber(spec.max_requests),
          per_seconds: capNumber(spec.per_seconds),
        };
      }
    }
    return side;
  }

  // The legacy `{tool: cap}` map, translated the way MCP's own
  // `legacy_caps_to_patch` does.
  for (const [key, cap] of Object.entries(plain)) {
    const n = capNumber(cap);
    if (n === undefined) continue;
    if (LEGACY_DEFAULT_KEYS.has(key)) side.tool_default = n;
    else if (key === "daily_total") side.max_calls_per_day = n;
    else side.tools[key] = n;
  }
  return side;
}

/** What a tool with no cap of its own falls to, from one side of a row. */
function inheritedText(side: QuotaSide): string {
  const td = side.tool_default ?? (side.doc ? -1 : undefined);
  return td === undefined ? "tier default" : `tier default (${capText(td)})`;
}

function laneText(spec: { max_requests?: number; per_seconds?: number } | undefined): string {
  if (!spec || spec.max_requests === undefined || spec.per_seconds === undefined) {
    return "server default";
  }
  return `${spec.max_requests} requests per ${spec.per_seconds}s`;
}

export interface QuotaChangeLine {
  /** A tool name (`tool: true`) or a tier-wide limit's label. */
  subject: string;
  tool: boolean;
  from: string;
  to: string;
}

export interface QuotaChange {
  /** The action in words: "Changed caps", "Removed an override". */
  verb: string;
  /** One per changed tool or limit. Empty for a write that changed nothing. */
  lines: QuotaChangeLine[];
}

/**
 * A quota audit row in words, or null for an action this console has no
 * words for (the caller then shows it generically, under its own name).
 *
 * `toolDefaultAtRemoval` is what a removed override fell to — a `delete_cap`
 * row does not record it. See `toolDefaultAt`.
 */
export function quotaAuditChange(
  e: AuditEvent,
  opts: { toolDefaultAtRemoval?: number } = {},
): QuotaChange | null {
  if (e.action === "delete_cap") {
    const target = auditTarget(e.target) ?? "";
    const tool = target.includes("/") ? target.slice(target.indexOf("/") + 1) : target;
    const previous = capNumber(auditPlain(e.before));
    const td = opts.toolDefaultAtRemoval;
    return {
      verb: "Removed an override",
      lines: [
        {
          subject: tool || "a tool",
          tool: tool !== "",
          from: previous === undefined ? "its own cap" : capText(previous),
          to: td === undefined ? "tier default" : `tier default (${capText(td)})`,
        },
      ],
    };
  }

  if (e.action !== "set_tier_config" && e.action !== "set_caps") return null;

  const isDoc = e.action === "set_tier_config";
  const before = quotaSide(e.before, isDoc);
  const after = quotaSide(e.after, isDoc);
  const caps: QuotaChangeLine[] = [];
  const lanes: QuotaChangeLine[] = [];

  // The tier-wide limits first: they reach the most tools. A legacy row only
  // names one when it wrote it.
  const tierWide = [
    ["tool_default", "not set (unlimited)"],
    ["max_calls_per_day", "not set (no daily total)"],
  ] as const;
  for (const [key, unset] of tierWide) {
    const b = before[key];
    const a = after[key];
    if (b === a) continue;
    if (!before.doc && !after.doc && (b === undefined || a === undefined)) continue;
    caps.push({
      subject: TIER_WIDE_LABEL[key]!,
      tool: false,
      from: b === undefined ? unset : capText(b),
      to: a === undefined ? unset : capText(a),
    });
  }

  const tools = [...new Set([...Object.keys(before.tools), ...Object.keys(after.tools)])].sort();
  for (const tool of tools) {
    const b = before.tools[tool];
    const a = after.tools[tool];
    if (b === a) continue;
    caps.push({
      subject: tool,
      tool: true,
      from: b === undefined ? inheritedText(before) : capText(b),
      to: a === undefined ? inheritedText(after) : capText(a),
    });
  }

  const laneNames = [
    ...new Set([...Object.keys(before.ratelimit), ...Object.keys(after.ratelimit)]),
  ].sort();
  for (const lane of laneNames) {
    const from = laneText(before.ratelimit[lane]);
    const to = laneText(after.ratelimit[lane]);
    if (from !== to) lanes.push({ subject: `Rate limit, ${lane} lane`, tool: false, from, to });
  }

  const verb =
    lanes.length > 0 && caps.length > 0
      ? "Changed caps and rate limits"
      : lanes.length > 0
        ? "Changed rate limits"
        : "Changed caps";
  return { verb, lines: [...caps, ...lanes] };
}

function stampMs(timestamp: string | null | undefined): number | null {
  if (!timestamp) return null;
  const ms = Date.parse(timestamp);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * The tier's `tool_default` at a moment, from its own audit trail.
 *
 * Only a document write moves the default, and its `before` is the document
 * as it stood — so the default at `at` is the `before` of the first
 * `set_tier_config` after it. A removal rewrites the document too, but only
 * its `tools`. With no document write since, the default is `current`.
 * Undefined when `at` is unknown. An absent default in a document is
 * unlimited, so a `before` without one is -1.
 */
export function toolDefaultAt(
  events: readonly AuditEvent[],
  at: string | null | undefined,
  current: number | undefined,
): number | undefined {
  const t = stampMs(at);
  if (t === null) return undefined;
  const next = events
    .filter((e) => e.action === "set_tier_config")
    .map((e) => ({ e, ms: stampMs(e.timestamp) }))
    .filter((x): x is { e: AuditEvent; ms: number } => x.ms !== null && x.ms > t)
    .sort((a, b) => a.ms - b.ms)[0];
  if (!next) return current;
  const side = quotaSide(next.e.before, true);
  return side.tool_default ?? -1;
}

/** A row's identity, for showing a row that two reads both returned once. */
function auditRowKey(e: AuditEvent): string {
  const sk = auditTarget(e.sk);
  if (sk) return sk;
  return JSON.stringify([e.timestamp, e.action, auditTarget(e.target), e.request_id ?? null]);
}

/**
 * One tier's quota history, newest first, from the two reads that hold it.
 *
 * `GET /admin/audit/quota/<tier>` is an exact match on the target, so it has
 * the tier's document writes and never a removal — those are targeted
 * `<tier>/<tool>` (DA-3706). `GET /admin/audit/quota` lists the whole domain,
 * and the rows under `<tier>/` are the removals. The by-target route ignores
 * its `{domain}` segment, so its rows are also kept to the quota domain.
 */
export function tierQuotaHistory(
  ownRows: readonly AuditEvent[],
  domainRows: readonly AuditEvent[],
  tier: string,
): AuditEvent[] {
  const mine = (e: AuditEvent) => {
    const target = auditTarget(e.target);
    return target === tier || (target?.startsWith(`${tier}/`) ?? false);
  };
  const seen = new Set<string>();
  const out: AuditEvent[] = [];
  for (const e of [
    ...ownRows.filter((e) => e.domain === "quota"),
    ...domainRows.filter((e) => e.domain === "quota" && mine(e)),
  ]) {
    const key = auditRowKey(e);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out.sort((a, b) => (stampMs(b.timestamp) ?? -Infinity) - (stampMs(a.timestamp) ?? -Infinity));
}

// -------------------------------------------------------- fleet metrics ----
//
// `GET /admin/fleet/metrics` (one bundled response, one `generated_at` for
// all four fields at once) is GONE. MCP replaced it with `GET
// /admin/metrics/{metric}` — one metric per call, envelope `{metric, value}`,
// no shared timestamp because there is no longer one read that produces all
// of them together. Seven metrics exist today; the original four plus three
// nothing in the console reads yet.
//
// Each call is independent, so each gets its own `useResource` and its own
// loading/error state — one metric failing must not blank the other six.

export const FLEET_METRICS = [
  "usage_today",
  "tier_distribution",
  "blocked",
  "admin_actions_today",
  "tenants_count",
  "top_tools_today",
  "quota_overrides_count",
] as const;
export type FleetMetricKey = (typeof FLEET_METRICS)[number];

/** The `value` shape per metric — matches `admin/services/fleet.py::metric()` exactly. */
export interface FleetMetricValue {
  usage_today: { day: string; total_credits: number };
  /**
   * Tenant count per tier. Merchants with no entitlements row are bucketed
   * under the literal key "none" — see TIER_DISTRIBUTION_UNSET_KEY, and do NOT
   * pass that key to `?tier=`.
   */
  tier_distribution: Record<string, number>;
  blocked: { merchants: number; users_everywhere: number };
  /**
   * Admin actions recorded today. A UTC CALENDAR DAY, not a rolling 24 hours —
   * it resets at UTC midnight, the same boundary as usage_today. Label it as
   * such: an operator in Riyadh reading "last 24 hours" at 02:00 would be
   * looking at a number that just reset.
   */
  admin_actions_today: number;
  /** Total merchants ever connected (a PROFILE row exists). */
  tenants_count: number;
  /** Top 10 tools by fleet-wide credit spend today, descending. */
  top_tools_today: { tool: string; credits: number }[];
  /** Explicit cap overrides per tier — the quotas table is empty by default. */
  quota_overrides_count: Record<string, number>;
}

export interface FleetMetricEnvelope<K extends FleetMetricKey = FleetMetricKey> {
  metric: K;
  /**
   * Optional for the reason every other field on every other response here
   * is: `client.ts` casts with `as T` and validates nothing, so a truncated
   * or unexpected 200 body arrives typed as a valid envelope with `value`
   * actually undefined. Six tiles then did `.value.toLocaleString()` or
   * `Object.entries(.value)` and took the whole page down.
   *
   * Read it through `metricValue`, never directly.
   */
  value?: FleetMetricValue[K];
}

/** `GET /mcp/admin/metrics/{key}` — one metric, one request. */
export function metricPath(key: FleetMetricKey): string {
  return `/mcp/admin/metrics/${key}`;
}

/**
 * A metric's value from its resource state, or null when there isn't one.
 *
 * ONE accessor for all seven metrics, because the failure is identical in
 * every case and a per-tile guard is a per-tile thing to forget: a metric that
 * is still loading, that failed, or that came back without a `value` all mean
 * the same thing to a tile — there is no number to draw yet.
 *
 * Takes the Resource rather than the envelope so the `kind === "ready"` check
 * cannot be skipped at a call site either.
 */
export function metricValue<K extends FleetMetricKey>(
  state: { kind: string; data?: FleetMetricEnvelope<K> },
): FleetMetricValue[K] | null {
  if (state.kind !== "ready") return null;
  return state.data?.value ?? null;
}


// ------------------------------------------------------------- MCP clients --
//
// Which AI apps may connect to MCP: the allowlist of OAuth redirect URIs.
// Source of truth: MCP admin/services/mcp_clients.py (read() / add() / remove() /
// requested()). Every field optional, for the reason at the top of CLAUDE.md:
// client.ts validates nothing, so these are assertions; read them through the
// helpers below.

/** `GET /admin/mcp-clients`. */
export interface McpClients {
  /** Fixed: Claude, ChatGPT connectors, local desktop apps. Not editable here. */
  baseline?: string[];
  /** Added from the console; removable. */
  extra?: string[];
  /** baseline ∪ extra: what MCP actually checks. */
  effective?: string[];
  max_extra?: number;
  live_within_seconds?: number;
  note?: string;
}

/** One app MCP refused for its redirect URI (`GET /admin/mcp-clients/requested`). */
export interface McpRequestedApp {
  redirect_uri?: string;
  /** What the app said about itself; null when it said nothing. */
  client_name?: string | null;
  client_id?: string | null;
  tries?: number;
  first_seen?: string | null;
  last_seen?: string | null;
  /** "register" or "authorize": where MCP refused it. */
  via?: string | null;
  /** false = an unfamiliar domain. Check it before allowing (phishing risk). */
  known_host?: boolean;
  /** false = POST would refuse this address anyway; `problem` says why. */
  can_allow?: boolean;
  problem?: string | null;
}

export interface McpRequestedApps {
  requested?: McpRequestedApp[];
  total?: number;
  /** Requests whose address has been allowed since (no longer listed). */
  now_allowed?: number;
  as_of?: string;
  note?: string;
}

/** `POST` / `DELETE /admin/mcp-clients` response. */
export interface McpClientWriteResponse {
  uri?: string;
  before?: string[];
  after?: string[];
  known_host?: boolean;
  live_within_seconds?: number;
}

export const MCP_CLIENTS_PATH = "/mcp/admin/mcp-clients";
export const MCP_CLIENTS_REQUESTED_PATH = "/mcp/admin/mcp-clients/requested";

/** The three lists, always arrays. */
export function mcpClientsOf(c: McpClients | undefined | null): {
  baseline: string[];
  extra: string[];
  effective: string[];
  maxExtra: number;
  liveWithinSeconds: number | undefined;
} {
  return {
    baseline: c?.baseline ?? [],
    extra: c?.extra ?? [],
    effective: c?.effective ?? [],
    maxExtra: c?.max_extra ?? 25,
    liveWithinSeconds: c?.live_within_seconds,
  };
}

/** The requested apps with every field defaulted, newest first as MCP sends them. */
export function requestedAppsOf(r: McpRequestedApps | undefined | null): Array<{
  redirectUri: string;
  name: string | null;
  tries: number;
  firstSeen: string | null;
  lastSeen: string | null;
  knownHost: boolean;
  canAllow: boolean;
  problem: string | null;
}> {
  return (r?.requested ?? [])
    .filter((a) => typeof a.redirect_uri === "string" && a.redirect_uri !== "")
    .map((a) => ({
      redirectUri: a.redirect_uri as string,
      name: a.client_name ?? null,
      tries: a.tries ?? 0,
      firstSeen: a.first_seen ?? null,
      lastSeen: a.last_seen ?? null,
      // Missing means "not known to be safe": the warning is the safe default.
      knownHost: a.known_host === true,
      canAllow: a.can_allow !== false,
      problem: a.problem ?? null,
    }));
}

/** The host of a redirect URI, for display ("gemini.google.com"); the URI if unparsable. */
export function redirectHost(uri: string): string {
  try {
    return new URL(uri.replace(":*", "")).hostname || uri;
  } catch {
    return uri;
  }
}

// ------------------------------------------------- availability (paused) ---
//
// `GET` / `POST /admin/availability` (MCP admin/routes/availability.py,
// admin/services/availability.py). The stored value is a LIST OF ENTRIES:
// tool names from `GET /admin/catalog`, plus `reports` for every tool whose
// name ends in `_report` (including reports MCP adds later). A POST REPLACES
// THE WHOLE LIST — there is no add-one / remove-one.
//
// Pausing is not a plan change: a paused tool keeps its caps on every tier.
// Never show a paused tool as "gated".

export const AVAILABILITY_PATH = "/mcp/admin/availability";
/** The newest row of this is the Paused tools panel's header line. */
export const AVAILABILITY_AUDIT_PATH = "/mcp/admin/audit/availability";
/** The entry that pauses every report tool, present and future. */
export const ALL_REPORTS = "reports";
/** The admin-audit action a paused-tools write records. */
export const PAUSED_TOOLS_ACTION = "set_paused_tools";
/** The live, editable message a paused tool's refusal carries (MCP #337). */
export const PAUSED_MESSAGE_KEY = "availability/paused_message";

/** `GET /admin/availability`. Every field optional/unknown; read via the helpers. */
export interface McpAvailability {
  /** Exactly what is stored: entries, not expanded. `[]` when nothing is paused. */
  paused?: unknown;
  /** What `paused` pauses, expanded against the catalog, in catalog order. */
  tools?: unknown;
  live_within_seconds?: unknown;
  note?: unknown;
}

/** `POST /admin/availability` 200. */
export interface McpAvailabilityWrite {
  before?: unknown;
  after?: unknown;
  tools?: unknown;
  live_within_seconds?: unknown;
}

function nameList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x !== "") : [];
}

/** The stored entries (`reports` and/or tool names). Always an array of strings. */
export function pausedOf(a: { paused?: unknown } | undefined | null): string[] {
  return nameList(a?.paused);
}

/** The tools the entries pause — for a GET or a POST response. */
export function pausedToolsOf(a: { tools?: unknown } | undefined | null): string[] {
  return nameList(a?.tools);
}

/** `live_within_seconds` when it is a number, else undefined ("shortly"). */
export function liveWithinSecondsOf(
  a: { live_within_seconds?: unknown } | undefined | null,
): number | undefined {
  const n = a?.live_within_seconds;
  return typeof n === "number" && Number.isFinite(n) ? n : undefined;
}

/** MCP's own rule for a report tool. */
export function isReportTool(name: string): boolean {
  return name.endsWith("_report");
}

/**
 * The tools a list of entries pauses: catalog order (as MCP's `_tools`), then
 * any named entry the catalog does not list, so a stale stored name is still
 * visible rather than silently dropped.
 */
export function expandPaused(entries: readonly string[], catalog: readonly string[]): string[] {
  const all = entries.includes(ALL_REPORTS);
  const named = new Set(entries.filter((e) => e !== ALL_REPORTS));
  const inCatalog = catalog.filter((t) => named.has(t) || (all && isReportTool(t)));
  const known = new Set(catalog);
  return [...inCatalog, ...[...named].filter((n) => !known.has(n))];
}

/**
 * What replacing `stored` with `next` does, in TOOLS, plus what happens to the
 * `reports` entry (which also covers reports MCP adds later, so it is a change
 * even when no current tool moves).
 *
 * `pauses` decides the confirmation level: anything newly cut off from every
 * merchant is L3; a change that only resumes is L2.
 */
export function pauseChange(
  stored: readonly string[],
  next: readonly string[],
  catalog: readonly string[],
): {
  pausing: string[];
  resuming: string[];
  allReports: "added" | "removed" | null;
  pauses: boolean;
} {
  const before = expandPaused(stored, catalog);
  const after = expandPaused(next, catalog);
  const had = stored.includes(ALL_REPORTS);
  const has = next.includes(ALL_REPORTS);
  const allReports = had === has ? null : has ? "added" : "removed";
  const pausing = after.filter((t) => !before.includes(t));
  const resuming = before.filter((t) => !after.includes(t));
  return { pausing, resuming, allReports, pauses: pausing.length > 0 || allReports === "added" };
}

/**
 * The names a 422 refused (`{"code": "unprocessable", "unknown": [...]}`).
 * `client.ts` flattens `detail` into the body; the `detail` branch is for a
 * body that arrives unflattened.
 */
export function unknownToolsOf(body: unknown): string[] {
  if (body === null || typeof body !== "object") return [];
  const b = body as { unknown?: unknown; detail?: unknown };
  const direct = nameList(b.unknown);
  if (direct.length > 0) return direct;
  const d = b.detail;
  return d !== null && typeof d === "object" ? nameList((d as { unknown?: unknown }).unknown) : [];
}

/** A paused list (an audit row's before/after) in words. */
export function pausedListText(v: unknown): string {
  if (!Array.isArray(v)) return auditValue(v);
  if (v.length === 0) return "nothing paused";
  return v.map((e) => (e === ALL_REPORTS ? "all reports" : auditValue(e))).join(", ");
}

// ------------------------------------------------------ blocked IPs (WAF) ---
//
// `GET` / `POST` / `DELETE /admin/waf/blocked-ips` (MCP admin/routes/waf.py,
// admin/services/waf.py). The WAF refuses a blocked IP or range on EVERY path of
// the MCP server, sign-in included. Unlike paused tools, a write is ONE entry:
// POST adds a CIDR, DELETE removes one, and both send `{cidr, reason}` — the
// DELETE in a JSON body, so the unblock is attributed too.

export const WAF_BLOCKED_IPS_PATH = "/mcp/admin/waf/blocked-ips";

/** `GET /admin/waf/blocked-ips`. Every field unknown; read through `blockedIpsOf`. */
export interface McpBlockedIps {
  blocked?: unknown;
  live_within_seconds?: unknown;
  note?: unknown;
}

/** `POST` 201 / `DELETE` 200: the one entry changed and that IP version's list. */
export interface McpBlockedIpWrite {
  cidr?: unknown;
  version?: unknown;
  before?: unknown;
  after?: unknown;
  live_within_seconds?: unknown;
}

/** Who last blocked or unblocked a CIDR, from the admin audit; the WAF keeps none. */
export interface BlockedIpChange {
  actor: string | null;
  reason: string | null;
  at: string | null;
}

export interface BlockedIp {
  cidr: string;
  version: string | null;
  /** null when no audit row names this CIDR. */
  lastChange: BlockedIpChange | null;
}

const textOrNull = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

/** The blocked entries, IPv4 first as MCP sends them. Total: any shape gives an array. */
export function blockedIpsOf(r: { blocked?: unknown } | undefined | null): BlockedIp[] {
  const rows = Array.isArray(r?.blocked) ? r.blocked : [];
  return rows.flatMap((row): BlockedIp[] => {
    if (!row || typeof row !== "object") return [];
    const { cidr, version, last_change } = row as Record<string, unknown>;
    if (typeof cidr !== "string" || cidr === "") return [];
    const c = last_change && typeof last_change === "object" ? (last_change as Record<string, unknown>) : null;
    return [
      {
        cidr,
        version: textOrNull(version),
        lastChange: c ? { actor: textOrNull(c.actor), reason: textOrNull(c.reason), at: textOrNull(c.at) } : null,
      },
    ];
  });
}
